import * as WebBrowser from "expo-web-browser";
import * as Application from "expo-application";
import { useActionSheet } from "@/components/hooks/useActionSheet";
import Text from "@/components/shared/AppText";
import React, { useEffect, useState } from "react";
import { Platform, View, Pressable } from "react-native";
import moment from "moment";
import { useAuth } from "../../AuthContext";
import AppActivityIndicator from "../../shared/AppActivityIndicator";
import AppHeader from "../../shared/AppHeader";
import AppTitle from "../../shared/AppTitle";
import AppContent from "../../shared/AppContent";
import * as Linking from "expo-linking";
import tw from "twrnc";
import { Ionicons } from "@expo/vector-icons";

import { deleteAccount } from "../../http/events";
import {
  fetchBillingPortalUrl,
  legacyBillingPortalLink,
} from "../../http/stripeLinks";
import { AccountTypePill } from "../../shared/DashboardHeader";
import Container from "@/components/shared/AppContainer";
import { currentUpdateLabel } from "@/components/updates";
import { openSupport, supportEnabled } from "@/components/support/config";
import CountBadge from "@/components/settings/CountBadge";
import { useWhatsNew } from "@/components/whatsNew/store";
import { libraryAvailableFor } from "@/components/library/config";
import { ownReportStarted } from "@/components/viewerHours/logic";
import LanguagePicker from "@/components/shared/LanguagePicker";
import { t, tn } from "@/localization";
import AccountSwitcher from "@/components/teams/AccountSwitcher";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import { hasOwnTeam } from "@/components/teams/flag";

const environment = process.env.NODE_ENV;

const Profile = ({ navigation, route }) => {
  const [loading, setloading] = useState(false);
  const { showActionSheetWithOptions } = useActionSheet();
  const {
    credits,
    name,
    email,
    first_name,
    last_name,
    signOut,
    refetchProfile,

    account_type,
    setFirstLoad,
    subscription_status,
    subscription_access_expiry_datetime,
    subscription_plan,
    stripeCustomerId,
  } = useAuth();

  const subActive =
    subscription_access_expiry_datetime > moment().utc().format();

  const env = environment === "production" ? "prod" : "test";
  const appVersion =
    Platform.OS === "web"
      ? null
      : [
          Application.nativeApplicationVersion,
          Application.nativeBuildVersion
            ? `(${Application.nativeBuildVersion})`
            : null,
        ]
          .filter(Boolean)
          .join(" ");
  const updateLabel = currentUpdateLabel();
  const appVersionLabel = appVersion
    ? updateLabel
      ? t("{platform} version {version} · update {update}", {
          platform: Platform.OS === "ios" ? "iOS" : "Android",
          version: appVersion,
          update: updateLabel,
        })
      : t("{platform} version {version}", {
          platform: Platform.OS === "ios" ? "iOS" : "Android",
          version: appVersion,
        })
    : null;

  // Stripe's billing portal for this account. evt.live checks the sign-in;
  // if that request fails, the old link emails the user a portal link instead.
  const openBillingPortal = async () => {
    // On the web, open the tab during the click so it isn't blocked as a pop-up.
    const tab = Platform.OS === "web" ? window.open("", "_blank") : null;
    let url;
    setloading(true);
    try {
      url = await fetchBillingPortalUrl(env);
    } catch (error) {
      url = legacyBillingPortalLink(env, stripeCustomerId);
    } finally {
      setloading(false);
    }
    if (Platform.OS !== "web") {
      Linking.openURL(url);
    } else if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  const review = async () => {
    const isAndroid =
      (Platform.OS === "web" &&
        navigator.userAgent.toLowerCase().indexOf("android") !== -1) ||
      Platform.OS === "android";

    await WebBrowser.openBrowserAsync(
      isAndroid
        ? "https://play.google.com/store/apps/details?id=pro.eventlive&reviewId=0"
        : "https://apps.apple.com/us/app/eventlive-livestream/id1457137042?ls=1&action=write-review"
    );
  };

  const deleteAcc = async () => {
    setloading(true);
    const options = [
      t(
        "Delete my account, all personal data, and all video files streamed or uploaded online from EventLive servers"
      ),
      t("Cancel"),
    ];

    const destructiveButtonIndex = 0;
    const cancelButtonIndex = 1;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
        title: t("Delete your Account? THIS ACTION IS IRREVERSIBLE"),
      },
      async (buttonIndex) => {
        if (buttonIndex === destructiveButtonIndex) {
          try {
            const r = await deleteAccount();
            console.log(r);
            await signOut();
            setFirstLoad(true);
          } catch (err) {
            console.log(err);
            setloading(false);
          }
        }
        if (buttonIndex === cancelButtonIndex) {
          setloading(false);
        }
      }
    );
  };

  const refetchStats = async () => {
    setloading(true);
    await refetchProfile();
    setloading(false);
  };

  useEffect(() => {
    refetchStats();
  }, []);

  useEffect(() => {
    return navigation.addListener("focus", () => {
      refetchStats();
    });
  }, [navigation]);

  const whatsNew = useWhatsNew();
  // Teams (hidden beta): the account switcher, and Team for your own account
  // or, as an admin, for the account you're working in.
  const team = useTeamAccess();
  // Team: business accounts manage their own; in someone else's account,
  // admins see the owner's.
  const showTeam =
    team.enabled &&
    (team.isShared ? team.can("manageTeam") : hasOwnTeam(account_type));

  const ListItem = ({ onPress, children, icon, right, disabled = false }) => (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={tw`w-full py-4 px-4 border-b border-gray-200 active:bg-gray-50 flex-row items-center ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <Ionicons name={icon} size={22} color="#3F5B7A" style={tw`mr-3`} />
      <Text style={tw`text-base flex-1`}>{children}</Text>
      {right}
      <Ionicons name="chevron-forward" size={20} color="#AEAEB2" />
    </Pressable>
  );

  return (
    <Container>
      <AppHeader hasRightButton={false} />
      <AppContent>
        <View
          style={tw`flex-1 flex-col mx-11 max-w-[600px] w-full self-center`}
        >
          <View style={tw`mx-4`}>
            <AppTitle
              text={
                !!first_name && !!last_name
                  ? `${first_name} ${last_name}`
                  : t("Profile")
              }
              style={tw`text-left my-5`}
            />
            <Text style={tw`text-gray-600 text-base -mt-4 mb-5`}>{email}</Text>

            {account_type === "business" ? (
              <AccountTypePill
                credits={credits}
                subActive={subActive}
                onPress={async () => {
                  navigation.navigate("Plans", { source: "profile" });
                }}
              />
            ) : (
              credits > 0 && (
                <Text style={tw`text-gray-600 text-base`}>
                  {tn(
                    "You have {count} prepaid event. It's used automatically when you create an event.",
                    "You have {count} prepaid events. They're used automatically when you create an event.",
                    credits
                  )}
                </Text>
              )
            )}
          </View>

          {team.enabled && (
            <AccountSwitcher navigation={navigation} style={tw`mt-6 mx-1`} />
          )}

          <View style={tw`flex-1 my-8`}>
            {showTeam && (
              <ListItem
                onPress={() => navigation.navigate("Team")}
                icon="people-outline"
                disabled={loading}
              >
                {team.isShared
                  ? t("{team}'s Team", { team: team.name })
                  : t("Team")}
              </ListItem>
            )}

            {account_type === "business" && (
              <ListItem
                onPress={() =>
                  navigation.navigate("Plans", { source: "profile" })
                }
                icon="card-outline"
                disabled={loading}
              >
                {t("EventLive Plans")}
              </ListItem>
            )}

            {account_type === "business" &&
              !team.isShared &&
              ownReportStarted() && (
                <ListItem
                  onPress={() => navigation.navigate("ViewerHours")}
                  icon="stats-chart-outline"
                  disabled={loading}
                >
                  {t("Viewer-hours")}
                </ListItem>
              )}

            {account_type === "business" && (
              <ListItem
                onPress={() => navigation.navigate("VideoWall")}
                icon="videocam-outline"
                disabled={loading}
              >
                {t("My Video Wall")}
              </ListItem>
            )}

            {libraryAvailableFor(account_type) && (
              <ListItem
                onPress={() =>
                  navigation.navigate("DownloadEverything", {
                    source: "profile",
                  })
                }
                icon="cloud-download-outline"
                disabled={loading}
              >
                {t("Download Everything")}
              </ListItem>
            )}

            {whatsNew.enabled && whatsNew.entries.length > 0 && (
              <ListItem
                onPress={() =>
                  navigation.navigate("WhatsNew", { source: "profile" })
                }
                icon="sparkles-outline"
                right={
                  <CountBadge
                    count={whatsNew.ready ? whatsNew.unreadCount : 0}
                    style={tw`mr-2`}
                  />
                }
                disabled={loading}
              >
                {t("What's New")}
              </ListItem>
            )}

            <ListItem
              onPress={() => navigation.navigate("UpdateEmail")}
              icon="person-outline"
              disabled={loading}
            >
              {t("Update Profile")}
            </ListItem>

            {subActive && (
              <ListItem
                onPress={openBillingPortal}
                icon="receipt-outline"
                disabled={loading}
              >
                {t("View Invoices and Manage Subscription")}
              </ListItem>
            )}

            <ListItem
              onPress={() => navigation.navigate("ForgotPassword", { email })}
              icon="key-outline"
              disabled={loading}
            >
              {t("Reset Password")}
            </ListItem>

            {supportEnabled && (
              <ListItem
                onPress={openSupport}
                icon="chatbubble-ellipses-outline"
                disabled={loading}
              >
                {t("Contact Support")}
              </ListItem>
            )}

            <LanguagePicker disabled={loading} />

            <ListItem onPress={review} icon="star-outline" disabled={loading}>
              {t("Review EventLive")}
            </ListItem>

            <ListItem
              onPress={deleteAcc}
              icon="trash-outline"
              disabled={loading}
            >
              {t("Delete Account")}
            </ListItem>

            <ListItem
              onPress={signOut}
              icon="log-out-outline"
              disabled={loading}
            >
              {t("Sign Out")}
            </ListItem>

            {!!appVersionLabel && (
              <Text style={tw`text-center text-xs text-gray-400 mt-5 mb-2`}>
                {appVersionLabel}
              </Text>
            )}
          </View>

          {loading && (
            <View
              style={tw`absolute inset-0 bg-white/50 items-center justify-center`}
            >
              <AppActivityIndicator />
            </View>
          )}
        </View>
      </AppContent>
    </Container>
  );
};

export default Profile;
